/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import * as React from "react";
import {SyntheticEvent, useEffect, useState} from "react";
import {Tab, Tabs} from "@mui/material";
import {styled} from "@mui/material/styles";
import {TooltipOnOverflow} from "./TooltipOnOverflow";
import {Resizable, ResizeCallbackData} from "react-resizable";
import 'react-resizable/css/styles.css'
import {usePostRequest, useReadWriteFavorites} from "../../../../api/helper";
import {NumberParam, StringParam, useQueryParam, withDefault} from "use-query-params";
import {useAppSelector} from "../../../../redux/hooks";
import {selectAccessControlState, selectCurrentUser} from "../../../../redux/ducks/AccessControl/slice";
import toast from "react-hot-toast";
import {TRACKED_ITEMS_ENDPOINT} from "../../../../api/constants";
import {selectGlobalDialogState} from "../../../../redux/ducks/GlobalDialog/slice";

interface StyledTabsProps {
  children?: React.ReactNode;
  value: number;
  onChange: (event: React.SyntheticEvent, newValue: number) => void;
}

const StyledTabs = styled((props: StyledTabsProps) => (
  <Tabs
    {...props}
    TabIndicatorProps={{children: <span className="MuiTabs-indicatorSpan"/>}}
    orientation="vertical"
    variant="scrollable"
    style={{}}
  />
))({
  '& .MuiTabs-indicator': {
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'transparent',
  },
  '& .MuiTabs-indicatorSpan': {
    maxWidth: 40,
    width: '100%',
    backgroundColor: '#635ee7',
  },
});

interface ITab {
  name: string
  content: React.ReactNode
  trackedItemUid?: string
}

interface IProps {
  tabs: ITab[]
  favoriteType: string
  uid: string
  onTabSelected?: any
}

const CustomTabWithTooltip = (props: any) => {
  return (
    <TooltipOnOverflow title={props?.label}>
      <Tab {...props}/>
    </TooltipOnOverflow>
  );
};
export const MyFiltersParam = withDefault(NumberParam, 0)

export const InnerVerticalTabs = (props: IProps) => {
  const fromDialog = useAppSelector(state => selectGlobalDialogState(state).isOpen)
  const [tabs, setTabs] = useState(props.tabs)
  const [width, setWidth] = useState(200)
  const {favoriteType, uid} = props
  const htabParamId = uid
  const [selectedTab, setSelectedTab] = useQueryParam(htabParamId, StringParam)
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(-1)

  useEffect(() => {
    if (props.tabs == null || props.tabs.length === 0) return
    setTabs(props.tabs)
    // handleTabChange(null, props.tabs.length - 1)
    /**
     * use value from url
     */
      // //if there is no id in the url then dont select a tab.
      // if(!selectedTab){
      //   setSelectedTabIndex(-1)
      //   return
      // }
    let _selectedTabIndex = 0 // use 0 as default for now rather than props.inittab
    // on initial render, if opening from a dialog, then we dont want to use the url param. otherwise, if it's there use it,
    // otherwise check first item and use that (could modify to check props.initindex, and if that dne then check index 0,
    // and if that doesn't work either set to 'baseline' or dont select anything).
    if (selectedTab != null) {
      _selectedTabIndex = getIndex(selectedTab, props.tabs)
      if (_selectedTabIndex == -1) {
        // invalid uid from url, so try to use the uid of first tab (could use props.inittab instead of 0)
        _selectedTabIndex = 0
        const _uid = props.tabs?.[0]?.trackedItemUid
        if (_uid) {
          setSelectedTab(_uid)
        }
        else setSelectedTab('baseline')
      }
      setSelectedTabIndex(_selectedTabIndex)
    } else {
      //if no uid in url, try to set tab 0 uid in url. since default tab is -1, once this runs the recently viewed useeffect will run
      const _uid = props.tabs?.[0]?.trackedItemUid
      if (_uid) {
        setSelectedTab(_uid)
      }
      else setSelectedTab('baseline')
      setSelectedTabIndex(0)
    }
    //if default tab is 0, and this turns out to be 0, then it wont rerendder and thus the recently viewed useeffect wont run.
  }, [props.tabs])
  const getIndex = (_uid: string, _tabs: any[]) => {
    return _tabs?.findIndex((x: any) => {
      return x?.trackedItemUid == _uid
    })
  }
  const handleTabChange = (event: React.SyntheticEvent | null, newValue: any) => {
    let val = tabs[newValue]?.trackedItemUid ?? 'baseline'
    setSelectedTab(val)
    //already have index=newValue, dont need this
    // let _selectedTabIndex = getIndex(val)
    // if(_selectedTabIndex == -1) _selectedTabIndex = 0
    if (val === 'baseline') setSelectedTabIndex(0)
    else setSelectedTabIndex(newValue)
    /**
     * If prop recently viewed enabled, make network call here.
     */

    // props?.onTabSelected(newValue)
  };

  const currentTabContent = tabs?.length > 0 ? tabs[selectedTabIndex]?.content : null
  const onResize = (event: SyntheticEvent, {node, size, handle}: ResizeCallbackData) => {
    setWidth(size.width)
  };

  return (
    // use height and width 100% as this is reusable. user should wrap in element to bound size.
    <div style={{display: 'flex', height: '100%', width: '100%', overflow: 'hidden'}}>
      <Resizable height={100} width={width} onResize={onResize}>
        <div style={{
          padding: 10,
          display: 'flex',
          flexFlow: 'column nowrap',
          overflow: 'hidden',
          width: width,
        }}>
          <Tabs
            orientation="vertical"
            variant="scrollable"
            style={{overflow: 'auto'}}
            value={selectedTabIndex}
            onChange={handleTabChange}
            aria-label="Vertical tabs example"
            // sx={{borderRight: 1, borderColor: 'divider', marginTop: 8, marginBottom: 8, width: 200}}
          >
            {tabs.map((tab: any, i: number) => {
              if (tab == null) return <></>
                return (
                  <CustomTabWithTooltip
                    key={i} label={tab.name} value={i}
                    iconPosition={'end'}
                    style={{
                      textOverflow: 'ellipsis',
                      // width: 150,
                      padding: 0, margin: 0,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      display: 'inline-block',
                      textAlign: 'left'
                    }}
                  />
                )
              }
            )}
          </Tabs>
        </div>
      </Resizable>

      <div style={{
        flexGrow: 1,
        margin: 10,
        overflow: 'auto'
      }}>
        {currentTabContent}
      </div>
    </div>
  )
}